import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Instagram, Linkedin, Facebook } from "lucide-react";
import { SEO } from "../components/shared/SEO";
import { PageHeader } from "../components/shared/PageHeader";
import { Reveal } from "../components/shared/Reveal";
import { Field, PrivacyCheck, SuccessBox } from "../components/shared/Form";
import { IMAGES } from "../content/site";
import { api, formatApiError } from "../lib/api";
import { useSettings, whatsappLink } from "../context/SettingsContext";

export default function Contatti() {
  const s = useSettings();
  const [f, setF] = useState({ name: "", email: "", phone: "", subject: "", message: "", privacy: false });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { type: "contact", name: f.name, email: f.email, phone: f.phone, message: f.message, details: { oggetto: f.subject }, privacy_accepted: f.privacy });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <>
      <SEO title="Contatti | Home Haven Srl Messina" description="Contatta Home Haven Srl per progetti immobiliari, opportunità di investimento o immobili da valorizzare. Sede a Messina, Via Consolare Pompea 75." image={IMAGES.living} />
      <PageHeader compact eyebrow="Contatti" title="Parliamo del tuo progetto immobiliare" image={IMAGES.living} />
      <section className="section bg-cream">
        <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5">
            <div className="eyebrow mb-6">{s.company_name}</div>
            <a href={`mailto:${s.email}`} data-testid="contact-email" className="block font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight hover:text-gold transition-colors break-all">{s.email}</a>
            <div className="mt-6 text-sm text-stone">PEC · <a href={`mailto:${s.pec}`} data-testid="contact-pec" className="hover:text-gold transition-colors">{s.pec}</a></div>
            {s.phone && <a href={`tel:${s.phone.replace(/\s/g, "")}`} data-testid="contact-phone" className="block mt-10 font-serif font-light text-3xl tracking-tight hover:text-gold transition-colors">{s.phone}</a>}
            {s.whatsapp_number && (
              <a href={whatsappLink(s)} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="btn mt-10 bg-sage text-white hover:bg-[#5b6a40]"><MessageCircle size={16} strokeWidth={1.5} /> Scrivici su WhatsApp</a>
            )}
            {s.address && <p className="mt-10 text-sm text-ink/70 font-light">{s.address}</p>}
            <div className="flex items-center gap-5 mt-12">
              {s.instagram && <a href={s.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="contact-instagram" className="text-ink/60 hover:text-gold transition-colors"><Instagram size={22} strokeWidth={1.3} /></a>}
              {s.linkedin && <a href={s.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="text-ink/60 hover:text-gold transition-colors"><Linkedin size={22} strokeWidth={1.3} /></a>}
              {s.facebook && <a href={s.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-ink/60 hover:text-gold transition-colors"><Facebook size={22} strokeWidth={1.3} /></a>}
            </div>
            <div className="mt-12 text-xs uppercase tracking-[0.18em] text-stone flex gap-6">
              <Link to="/privacy-policy" className="hover:text-gold">Privacy Policy</Link><Link to="/cookie-policy" className="hover:text-gold">Cookie Policy</Link>
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            {sent ? (
              <SuccessBox testId="contact-form-success" title="Messaggio inviato." text="Grazie per averci scritto. Ti risponderemo il prima possibile." />
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Field label="Nome e cognome" name="name" value={f.name} onChange={set} required testId="contact-name" />
                <Field label="Email" name="email" type="email" value={f.email} onChange={set} required testId="contact-email-input" />
                <Field label="Telefono" name="phone" type="tel" value={f.phone} onChange={set} testId="contact-phone-input" />
                <Field label="Oggetto" name="subject" as="select" value={f.subject} onChange={set} options={["Informazioni generali", "Proposta immobile", "Investimento", "Collaborazione professionale", "Altro"]} testId="contact-subject" />
                <div className="md:col-span-2"><Field label="Messaggio" name="message" as="textarea" rows={5} value={f.message} onChange={set} required testId="contact-message" /></div>
                <div className="md:col-span-2"><PrivacyCheck checked={f.privacy} onChange={(v) => set("privacy", v)} testId="contact-privacy" /></div>
                <div className="md:col-span-2"><button type="submit" disabled={busy} data-testid="contact-submit" className="btn-primary disabled:opacity-50">{busy ? "Invio…" : "Parliamo del tuo progetto"}</button></div>
              </form>
            )}
          </div>
        </div>
      </section>
      {s.show_map && s.maps_embed_url && (
        <section className="bg-white">
          <iframe title="Sede Home Haven" data-testid="contact-map" src={s.maps_embed_url} className="w-full h-[420px] grayscale" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </section>
      )}
    </>
  );
}
